import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useGT } from 'gt-react';
import { orpc, rpcClient } from '@/api/rpc/client';
import { useUserPreference } from '@/api/rpc/hooks/core/useUserPreference';
import { useToast } from '@/context/ToastContext';
import {
  KeyboardShortcutsPreferencesSchema,
  type GetUserPreferenceResponse,
  type KeyboardShortcutsPreferences,
} from '@erp/rpc';
import {
  applyHotkeyOverride,
  resolveHotkeyKeys,
  type HotkeyOverrides,
} from '@/lib/hotkeys/applyHotkeyOverride';
import {
  HOTKEY_REGISTRY,
  isHotkeyId,
  type HotkeyId,
} from '@/lib/hotkeys/registry';
import {
  createShortcutWriteQueue,
  type ShortcutWriteQueue,
} from '@/lib/hotkeys/shortcutWriteQueue';

// One queue for every mount of the hook. A settings-page remount must not
// start a second upsert that can land after an older snapshot.
let sharedQueue: ShortcutWriteQueue | undefined;
let reportWriteError: (() => void) | undefined;

const DEFAULTS: KeyboardShortcutsPreferences = {
  overrides: {},
};

const SHORTCUTS_QUERY_KEY = orpc.core.userPreferences.get.queryKey({
  input: { key: 'keyboardShortcuts' },
});

interface ResolvedHotkey {
  id: HotkeyId;
  keys: string;
  defaultKeys: string;
  isCustom: boolean;
}

export function useKeyboardShortcuts() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const gt = useGT();
  const { isLoading } = useUserPreference('keyboardShortcuts');
  useEffect(() => {
    reportWriteError = () => {
      void queryClient.invalidateQueries({ queryKey: SHORTCUTS_QUERY_KEY });
      showToast({
        color: 'error',
        message: gt('Could not save keyboard shortcuts. Try again.', {
          context: 'toast when saving a keyboard shortcut preference fails',
        }),
      });
    };
  }, [queryClient, showToast, gt]);
  const queue = shortcutWriteQueue();

  const overrides = currentOverrides(queryClient);

  const resolved: ResolvedHotkey[] = HOTKEY_REGISTRY.map((entry) => {
    const keys = resolveHotkeyKeys(overrides, entry.id);
    return {
      id: entry.id,
      keys,
      defaultKeys: entry.defaultKeys,
      isCustom: keys !== entry.defaultKeys,
    };
  });

  function persist(next: HotkeyOverrides) {
    queryClient.setQueryData<GetUserPreferenceResponse>(SHORTCUTS_QUERY_KEY, {
      key: 'keyboardShortcuts',
      value: { overrides: next },
    });
    queue.enqueue(next);
  }

  /**
   * Set (or clear, when keys is null) the override for one shortcut.
   * Returns the conflicting shortcut id when the combo is already in use.
   * Reads the query cache (not the render snapshot) so a second edit in the
   * same turn composes on the first instead of overwriting it.
   */
  function setBinding(id: HotkeyId, keys: string | null): HotkeyId | null {
    const result = applyHotkeyOverride(currentOverrides(queryClient), id, keys);
    if (result.conflictId) return result.conflictId;
    persist(result.overrides);
    return null;
  }

  function resetBinding(id: HotkeyId): HotkeyId | null {
    return setBinding(id, null);
  }

  function resetAll() {
    persist({});
  }

  return {
    resolved,
    getKeys: (id: HotkeyId) => resolveHotkeyKeys(overrides, id),
    setBinding,
    resetBinding,
    resetAll,
    isLoading,
    hasCustomBindings: Object.keys(overrides).some((id) => isHotkeyId(id)),
  };
}

function shortcutWriteQueue(): ShortcutWriteQueue {
  if (!sharedQueue) {
    sharedQueue = createShortcutWriteQueue(
      async (overrides) => {
        await rpcClient.core.userPreferences.upsert({
          key: 'keyboardShortcuts',
          value: { overrides },
        });
      },
      () => {
        reportWriteError?.();
      },
    );
  }
  return sharedQueue;
}

function currentOverrides(
  queryClient: ReturnType<typeof useQueryClient>,
): HotkeyOverrides {
  const cached =
    queryClient.getQueryData<GetUserPreferenceResponse>(SHORTCUTS_QUERY_KEY);
  return parsePreferences(cached?.value).overrides;
}

function parsePreferences(
  value: Record<string, unknown> | undefined,
): KeyboardShortcutsPreferences {
  if (!value) return DEFAULTS;
  const parsed = KeyboardShortcutsPreferencesSchema.safeParse(value);
  return parsed.success ? parsed.data : DEFAULTS;
}
